import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { PageMeta } from '@app/components/meta/Meta';
import { PageLayout } from '@app/components/page';
import { PageHeader } from '@app/components/page/PageHeader';
import { PageIntro } from '@app/components/page/PageIntro';
import { PageTitle } from '@app/components/page/PageTitle';
import { StyledTable } from '@app/components/table/Table';
import useBrands from '@app/hooks/brands/useBrands';
import useChannelPartnerActions from '@app/hooks/channelPartners/useChannelPartnerActions';
import useModal from '@app/hooks/useModal';
import type { Brand, BrandListParams } from '@app/services/api.types';
import { AppConfig } from '@app/utils/app-config';
import { fn } from '@app/utils';
import { url } from '@app/utils/constants';
import { Flex, type TableColumnsType } from 'antd';
import React, { useState } from 'react';
import { CgSoftwareDownload } from 'react-icons/cg';
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi2';
import { MdAdd } from 'react-icons/md';
import { Link } from 'react-router';
import { BrandAddModal } from './components/BrandAddModal';
import BrandDeleteModal from './components/BrandDeleteModal';
import { BrandEditModal } from './components/BrandEditModal';
import BrandFilters from './components/BrandFilters';
import BrandDrawer from './components/BrandDrawer';

export const BrandsList = () => {
    const [selectedRecord, setSelectedRecord] = useState<Brand | null>(null);
    const { exportChannelPartners } = useChannelPartnerActions();
    const [params, setParams] = useState<BrandListParams>({
        page: 1,
        per_page: AppConfig.pagination_size,
        search: '',
        type_id: [],
        brand_id: [],
        location_id: [],
        order_by: undefined,
        order_dir: undefined,
    });

    const { data: brandsData, isLoading } = useBrands(params);

    const modalAdd = useModal<Brand>({
        initialData: undefined,
    });

    const modalEdit = useModal<Brand>({
        initialData: undefined,
    });

    const modalRemove = useModal<Brand>({
        initialData: undefined,
    });

    const columns: TableColumnsType<Brand> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Channel Partner',
            dataIndex: 'channel_name',
            key: 'channel_name',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Admin Name',
            dataIndex: 'contact_name',
            key: 'contact_name',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Admin Email',
            dataIndex: 'contact_email',
            key: 'contact_email',
            sorter: true,
            render: (value) => value || '-',
        },
        {
            title: 'Admin Phone',
            dataIndex: 'contact_telephone',
            key: 'contact_telephone',
            sorter: true,
            render: (value) => value || '-',
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            sorter: false,
            width: 150,
            fixed: 'right',
            render: (_value, record) => (
                <StyledButtonGroup justify="flex-start">
                    <StyledButton
                        icon={<HiOutlinePencil />}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                            openEditModal(record);
                        }}>
                        Edit
                    </StyledButton>
                    <StyledButton
                        icon={<HiOutlineTrash />}
                        color="red"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                            modalRemove.open(record);
                        }}>
                        Delete
                    </StyledButton>
                </StyledButtonGroup>
            ),
        },
    ];

    const openEditModal = (record: Brand) => {
        modalEdit.open(record);
    };

    const closeEditModal = () => {
        modalEdit.reset();
    };

    const closeRemoveModal = () => {
        modalRemove.reset();
    };

    const handleExport = async () => {
        exportChannelPartners.mutate(params);
    };

    return (
        <React.Fragment>
            <PageMeta title="Brands" />

            <PageHeader
                breadcrumbItems={[
                    {
                        title: <Link to={url.adminPortal.dashboard}>Dashboard</Link>,
                    },
                    { title: 'Brands' },
                ]}>
                <Flex justify="space-between" gap={20}>
                    <div>
                        <PageTitle title="Brands" />
                        <PageIntro>
                            <p>Manage brand organisations</p>
                        </PageIntro>
                    </div>
                    <div>
                        <StyledButtonGroup>
                            <StyledButton
                                type="primary"
                                color="blue"
                                icon={<MdAdd size={20} />}
                                onClick={() => modalAdd.open()}>
                                Add New
                            </StyledButton>
                        </StyledButtonGroup>
                    </div>
                </Flex>
            </PageHeader>

            <PageLayout>
                <PageLayout.Content>
                    <Flex align="end" className="styled-table-header">
                        <BrandFilters
                            onChange={(values) => {
                                setParams((prev) => ({ ...prev, ...values, page: 1 }));
                            }}
                        />
                        <div>
                            <StyledButtonGroup>
                                <StyledButton
                                    type="primary"
                                    icon={<CgSoftwareDownload size={20} />}
                                    disabled={Boolean(!brandsData?.total)}
                                    onClick={handleExport}>
                                    Export CSV
                                </StyledButton>
                            </StyledButtonGroup>
                        </div>
                    </Flex>

                    <br />

                    <StyledTable<Brand>
                        columns={columns}
                        dataSource={brandsData?.data || []}
                        rowKey={({ id }) => id}
                        // scroll={{ x: 860 }}
                        loading={isLoading}
                        pagination={{
                            current: params.page,
                            pageSize: params.per_page,
                            total: brandsData?.total || 0,
                        }}
                        onChange={(pagination, _filters, sorter) => {
                            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
                            const order_by = singleSorter?.field as string | undefined;
                            const order_dir = fn.getTableOrderDir(
                                order_by,
                                singleSorter?.order ?? undefined
                            );
                            setParams((prev) => ({
                                ...prev,
                                page: pagination.current || 1,
                                order_by,
                                order_dir,
                            }));
                        }}
                        onRow={(record) => {
                            return {
                                onClick: () => setSelectedRecord(record),
                            };
                        }}
                        rowClassName={() => 'is-clickable'}
                    />
                </PageLayout.Content>
            </PageLayout>

            <BrandDrawer
                size={700}
                onClose={() => setSelectedRecord(null)}
                open={Boolean(selectedRecord)}
                data={selectedRecord}
            />

            <BrandAddModal
                open={modalAdd.isOpen}
                onCancel={() => modalAdd.reset()}
                onOk={() => modalAdd.reset()}
            />

            <BrandEditModal
                open={modalEdit.isOpen}
                data={modalEdit.data}
                onCancel={closeEditModal}
                onOk={closeEditModal}
            />

            <BrandDeleteModal
                open={modalRemove.isOpen}
                data={modalRemove.data}
                onCancel={closeRemoveModal}
                onOk={closeRemoveModal}
            />
        </React.Fragment>
    );
};

export default BrandsList;
